import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { fileUrl } from "@/lib/api";

let ctx = null;
const ac = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

function tone(freq, start, dur, type = "sine", vol = 0.25) {
  const c = ac();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(vol, c.currentTime + start);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + start + dur);
  o.connect(g).connect(c.destination);
  o.start(c.currentTime + start);
  o.stop(c.currentTime + start + dur + 0.05);
}

export function playSound(kind) {
  try {
    if (kind === "bid") tone(880, 0, 0.12, "triangle", 0.2);
    if (kind === "gavel") [0, 0.25, 0.5].forEach((t) => { tone(110, t, 0.18, "square", 0.35); tone(70, t, 0.25, "sine", 0.4); });
    if (kind === "sold") [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.8 + i * 0.13, 0.4, "triangle", 0.25));
    if (kind === "unsold") [330, 247].forEach((f, i) => tone(f, i * 0.3, 0.45, "sine", 0.25));
    if (kind === "spin") for (let i = 0; i < 26; i++) tone(1200, i * 0.16 * (1 + i / 30), 0.03, "square", 0.08);
  } catch (e) { /* audio unavailable */ }
}

export function fireConfetti() {
  const end = Date.now() + 1800;
  const colors = ["#E5B842", "#F5D061", "#FF5722", "#ffffff"];
  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0 }, colors });
    confetti({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

export function useAuctionFx(lastEvent, soundOn, confettiOn = true) {
  const seen = useRef(0);
  useEffect(() => {
    if (!lastEvent || lastEvent.at === seen.current) return;
    seen.current = lastEvent.at;
    const e = lastEvent.event;
    if (e === "sold") {
      if (soundOn) { playSound("gavel"); playSound("sold"); }
      if (confettiOn) fireConfetti();
    } else if (e === "unsold" && soundOn) playSound("unsold");
    else if (e === "bid" && soundOn) playSound("bid");
  }, [lastEvent, soundOn, confettiOn]);
}

export function SpinWheel({ wheel, soundOn }) {
  const [show, setShow] = useState(null);
  const [rot, setRot] = useState(0);
  useEffect(() => {
    if (!wheel?.names?.length) return undefined;
    setShow(wheel);
    setRot(0);
    if (soundOn) playSound("spin");
    const n = wheel.names.length;
    const a = 360 / n;
    const t1 = setTimeout(() => setRot(360 * 6 - (n - 0.5) * a), 50);
    const t2 = setTimeout(() => setShow(null), 6500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [wheel, soundOn]);
  if (!show) return null;
  const n = show.names.length;
  const a = 360 / n;
  const R = 240;
  const pt = (deg) => [R + R * Math.sin((deg * Math.PI) / 180), R - R * Math.cos((deg * Math.PI) / 180)];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm" data-testid="spin-wheel-overlay">
      <div className="text-center">
        <p className="eyebrow mb-4">Drawing the next player</p>
        <div className="relative mx-auto" style={{ width: R * 2, height: R * 2 }}>
          <div className="absolute left-1/2 -top-3 -translate-x-1/2 z-10 w-0 h-0 border-l-[16px] border-r-[16px] border-t-[28px] border-l-transparent border-r-transparent border-t-[#FF5722]" />
          <svg width={R * 2} height={R * 2} style={{ transform: `rotate(${rot}deg)`, transition: rot ? "transform 4.6s cubic-bezier(.17,.67,.12,1)" : "none" }}>
            {show.names.map((nm, i) => {
              const [x1, y1] = pt(i * a);
              const [x2, y2] = pt((i + 1) * a);
              const mid = (i + 0.5) * a;
              return (
                <g key={i}>
                  <path d={`M${R},${R} L${x1},${y1} A${R},${R} 0 ${a > 180 ? 1 : 0} 1 ${x2},${y2} Z`} fill={i % 2 ? "#0B1323" : "#132742"} stroke="#E5B842" strokeWidth="1.5" />
                  <text x={R} y={40} fill={i === n - 1 ? "#F5D061" : "#E2E8F0"} fontSize="13" fontWeight="700" textAnchor="middle" transform={`rotate(${mid} ${R} ${R})`}>
                    {nm.length > 16 ? nm.slice(0, 15) + "…" : nm}
                  </text>
                </g>
              );
            })}
            <circle cx={R} cy={R} r="38" fill="#E5B842" />
            <text x={R} y={R + 7} textAnchor="middle" fontSize="20" fontWeight="900" fill="#050B14">OPL</text>
          </svg>
        </div>
        <p className="mt-6 text-4xl font-black gold-text rise" style={{ animationDelay: "4.7s" }}>{show.names[n - 1]}</p>
      </div>
    </div>
  );
}

export function Stamp({ status }) {
  if (!["SOLD", "UNSOLD"].includes(status)) return null;
  const sold = status === "SOLD";
  return (
    <div className="absolute inset-x-0 top-24 grid place-items-center pointer-events-none z-10" data-testid="player-stamp">
      <div className={`stamp-in border-[6px] rounded-xl px-6 py-2 font-display font-black tracking-widest -rotate-12 ${sold ? "border-emerald-400 text-emerald-300 text-6xl bg-emerald-950/60" : "border-red-400 text-red-300 text-3xl bg-red-950/60"}`}>
        {sold ? "SOLD" : "REMAINED UNSOLD"}
      </div>
    </div>
  );
}

export function Sponsors({ sponsors, className = "" }) {
  if (!sponsors?.length) return null;
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="sponsor-strip">
      <span className="text-[9px] uppercase tracking-widest text-[#E5B842]/80">Sponsors</span>
      {sponsors.map((s) => (s.logo ? <img key={s.name} src={fileUrl(s.logo)} alt={s.name} title={s.name} className="h-8 max-w-[90px] object-contain" /> :
        <span key={s.name} className="text-xs font-bold text-slate-300">{s.name}</span>))}
    </div>
  );
}
